/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\pages\Auth\Login.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AuthContext from '../../contexts/AuthContext';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');
  const { login, error } = useContext(AuthContext);
  const navigate = useNavigate();

  const BETA_URL =
    process.env.REACT_APP_BETA_URL ||
    'https://so-nesting.com/?open=beta';
  const LAUNCH_URL =
    process.env.REACT_APP_LAUNCH_URL ||
    'https://so-nesting.com/#waitlist';

  const openExternalLink = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!email || !password) {
      setFormError('Veuillez remplir tous les champs');
      return;
    }

    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      // L'erreur est déjà gérée dans le contexte d'authentification
      console.error('Erreur de connexion:', err);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Connexion à votre compte
          </h2>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email-address" className="sr-only">
                Adresse email
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="input-field rounded-t-md"
                placeholder="Adresse email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Mot de passe
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="input-field rounded-b-md"
                placeholder="Mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          {(formError || error) && (
            <div className="text-red-500 text-sm text-center">
              {formError || error}
            </div>
          )}

          <div className="flex items-center justify-end text-sm">
            <Link to="/forgot-password" className="font-medium text-primary-600 hover:text-primary-500">
              Mot de passe oublie ?
            </Link>
          </div>

          <div>
            <button
              type="submit"
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Se connecter
            </button>
          </div>
        </form>

        <div className="mt-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm text-sm text-gray-700">
          <p className="font-semibold text-gray-900 text-center">Vous n’avez pas encore d’accès ?</p>
          <p className="mt-1 text-center text-xs text-amber-700">
            L’accès bêta implique des retours d’usage. Places limitées.
          </p>

          <div className="mt-3 space-y-2">
            <button
              type="button"
              onClick={() => openExternalLink(BETA_URL)}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-amber-500 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
              aria-label="Demander un accès bêta"
            >
              🧪 Demander un accès bêta
            </button>
            <p className="text-xs text-center text-gray-500">
              Programme bêta : destiné aux utilisateurs prêts à tester activement et partager leurs retours.
            </p>

            <button
              type="button"
              onClick={() => openExternalLink(LAUNCH_URL)}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              aria-label="Être informé du lancement"
            >
              🔔 Être informé du lancement
            </button>
            <p className="text-xs text-center text-gray-500">
              Recevoir un email lors de l’ouverture officielle.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
